import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { adopt } from 'react-adopt';
import { Mutation, Query } from 'components/common/Query';
import ContentLoader from 'react-content-loader';
import gql from 'graphql-tag';
import { CANCEL_PAIDAD_MUTATION } from 'components/PaidAd/List';
import { PAYMENT_REFUND_MUTATION } from 'components/PaidAd/Details';
import { paidAdStatus, paymentStatus } from 'config';
import { bulkUpdate } from 'helpers/updateMutationsHelper';
import PaymentDetails from './Details';

export const PAYMENT_DETAIL = gql`
  query PaymentDetail($id: ID!) {
    payment(id: $id) {
      id
      createdOn
      updatedOn
      amount
      currency
      refundedOn
      refundedAmount
      status
      type
      platformPaymentId
      user {
        email
        fullname
      }
      order {
        orderType
        ... on AdminCreditOrderType {
          agency {
            id
            fullname
            email
            phone
            credit
            ... on AgencyType {
              commissionLead
            }
          }
        }
        ... on AdminPaidAdOrderType {
          paidAd {
            id
            owner {
              id
              email
              fullname
              phone
            }
            ... on AdminAutoPaidAdType {
              status
              consumedCredit
            }
          }
        }
      }
    }
  }
`;

/**
 * Mutation to refund payment
 */
export const CREDIT_REFUND_MUTATION = gql`
  mutation PaymentCreditRefund($paymentId: ID!, $total: Boolean) {
    paymentCreditRefund(input: { paymentId: $paymentId, total: $total }) {
      errors {
        field
        messages
      }
    }
  }
`;

class DetailsContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: null,
      success: false,
      isTotalRefundPaidAd: false,
    };
    this.paidAdRefund = () => {};
    this.paidAdCancel = () => {};
    this.Compose = adopt({
      query: this.PaymentDetail,
      creditRefund: this.CreditRefund,
      paidAdRefund: this.PaidAdRefund,
      cancelPaidAd: this.CancelPaidAd,
    });
  }

  /**
   * @description
   * Payment Detail query .
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  PaymentDetail = ({ render }) => {
    const { id } = this.props;
    return (
      <Query
        query={PAYMENT_DETAIL}
        variables={{ id }}
        notifyOnNetworkStatusChange
        fetchPolicy="network-only"
      >
        {({ data, loading }) => render({ data, loading })}
      </Query>
    );
  };

  /**
   * @description
   * Mutation credit refund callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation render prop.
   */
  CreditRefund = ({ render }) => {
    const { id } = this.props;
    return (
      <Mutation
        mutation={CREDIT_REFUND_MUTATION}
        update={cache => {
          const data = {
            status: paymentStatus.PENDING_REFUND,
            __typename: 'AdminPaymentType',
          };

          bulkUpdate(cache, 'AdminPaymentType', 'Refund', 'status', data, [id]);
        }}
        onCompleted={this.PaymentRefundComplete}
      >
        {(handleCreditRefund, { loading }) =>
          render({ handleCreditRefund, loadingPaymentRefund: loading })
        }
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation callback when refund is completed.
   *
   * @param {Object} data - Mutation result.
   */
  PaymentRefundComplete = async ({ paymentCreditRefund }) => {
    const { errors } = paymentCreditRefund;
    if (errors) {
      this.setState({ errors });
    } else {
      this.setState({ success: true });
    }
  };

  /**
   * @description
   * Mutation paid ad refund callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation render prop.
   */
  PaidAdRefund = ({ render }) => {
    const { id } = this.props;
    return (
      <Mutation
        mutation={PAYMENT_REFUND_MUTATION}
        update={cache => {
          const data = {
            status: paymentStatus.PENDING_REFUND,
            __typename: 'AdminPaymentType',
          };

          bulkUpdate(cache, 'AdminPaymentType', 'Refund', 'status', data, [id]);
        }}
        onCompleted={this.PaidAdRefundComplete}
      >
        {handlePaidAdRefund => {
          this.paidAdRefund = handlePaidAdRefund;
          return render();
        }}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation callback when paid ad refund is completed.
   *
   * @param {Object} data - Mutation result.
   */
  PaidAdRefundComplete = async ({ paymentRefund }) => {
    const { errors } = paymentRefund;
    if (errors) {
      this.setState({ errors });
    } else {
      this.setState({ success: true });
    }
  };

  /**
   * @description
   * Mutation cancel callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  CancelPaidAd = ({ render }) => (
    <Mutation
      mutation={CANCEL_PAIDAD_MUTATION}
      onCompleted={this.CancelPaidAdCompleted}
    >
      {handlePaidAdCancel => {
        this.paidAdCancel = handlePaidAdCancel;
        return render();
      }}
    </Mutation>
  );

  /**
   * @description
   * Mutation callback when cancel paid ad is completed.
   *
   * @param {Object} data - Mutation result.
   */
  CancelPaidAdCompleted = async ({ cancelAutoPaidAd }) => {
    const { errors } = cancelAutoPaidAd;
    const { isTotalRefundPaidAd } = this.state;
    if (errors) {
      this.setState({ errors });
    } else {
      this.paidAdRefund({
        variables: {
          autoPaidAd: cancelAutoPaidAd.successIds[0],
          // eslint-disable-next-line react/destructuring-assignment
          total: isTotalRefundPaidAd,
        },
      });
    }
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ errors: null, success: false });
  };

  /**
   * @description
   * Handle Paid Ad refund logic
   *
   * @param {Object} data - Paid ad info.
   * @returns {undefined} - No returns value.
   */
  onPaidAdRefund = data => {
    const { id, total, status } = data;
    this.setState({ isTotalRefundPaidAd: total });

    if (status !== paidAdStatus.CANCELLED) {
      return this.paidAdCancel({ variables: { ids: [id] } });
    }
    return this.paidAdRefund({
      variables: {
        autoPaidAd: id,
        // eslint-disable-next-line react/destructuring-assignment
        total,
      },
    });
  };

  render() {
    const { errors, success } = this.state;
    return (
      <this.Compose>
        {({
          query: { data, loading },
          creditRefund: { handleCreditRefund },
        }) => {
          if (!loading) {
            return (
              <PaymentDetails
                data={data.payment}
                serverErrors={errors}
                success={success}
                onCloseToast={this.handleCloseNotification}
                onCreditRefund={variables => handleCreditRefund({ variables })}
                onRefundPaidAd={(id, total) =>
                  this.onPaidAdRefund({
                    id,
                    total,
                    status: data.payment.order.paidAd.status,
                  })
                }
              />
            );
          }
          return (
            <ContentLoader
              height={871}
              width={1073}
              speed={2}
              primaryColor="#d9d9d9"
              secondaryColor="#ecebeb"
            >
              <path
                // eslint-disable-next-line max-len
                d="M8,61 L292,61 C296.418278,61 300,64.581722 300,69 L300,71 C300,75.418278 296.418278,79 292,79 L8,79 C3.581722,79 5.41083001e-16,75.418278 0,71 L0,69 C-5.41083001e-16,64.581722 3.581722,61 8,61 Z M486,61 L554,61 C558.418278,61 562,64.581722 562,69 L562,71 C562,75.418278 558.418278,79 554,79 L486,79 C481.581722,79 478,75.418278 478,71 L478,69 C478,64.581722 481.581722,61 486,61 Z M590,61 L700,61 C704.418278,61 708,64.581722 708,69 L708,71 C708,75.418278 704.418278,79 700,79 L590,79 C585.581722,79 582,75.418278 582,71 L582,69 C582,64.581722 585.581722,61 590,61 Z M764,61 L1065,61 C1069.41828,61 1073,64.581722 1073,69 L1073,121 C1073,125.418278 1069.41828,129 1065,129 L764,129 C759.581722,129 756,125.418278 756,121 L756,69 C756,64.581722 759.581722,61 764,61 Z M764,153 L1065,153 C1069.41828,153 1073,156.581722 1073,161 L1073,661 C1073,665.418278 1069.41828,669 1065,669 L764,669 C759.581722,669 756,665.418278 756,661 L756,161 C756,156.581722 759.581722,153 764,153 Z M764,685 L1065,685 C1069.41828,685 1073,688.581722 1073,693 L1073,863 C1073,867.418278 1069.41828,871 1065,871 L764,871 C759.581722,871 756,867.418278 756,863 L756,693 C756,688.581722 759.581722,685 764,685 Z M8,111 L700,111 C704.418278,111 708,114.581722 708,119 L708,121 C708,125.418278 704.418278,129 700,129 L8,129 C3.581722,129 5.41083001e-16,125.418278 0,121 L0,119 C-5.41083001e-16,114.581722 3.581722,111 8,111 Z M8,137 L397,137 C401.418278,137 405,140.581722 405,145 L405,147 C405,151.418278 401.418278,155 397,155 L8,155 C3.581722,155 5.41083001e-16,151.418278 0,147 L0,145 C-5.41083001e-16,140.581722 3.581722,137 8,137 Z M8,226 L700,226 C704.418278,226 708,229.581722 708,234 L708,598 C708,602.418278 704.418278,606 700,606 L8,606 C3.581722,606 5.41083001e-16,602.418278 0,598 L0,234 C-5.41083001e-16,229.581722 3.581722,226 8,226 Z M8,187 L129,187 C133.418278,187 137,190.581722 137,195 L137,197 C137,201.418278 133.418278,205 129,205 L8,205 C3.581722,205 5.41083001e-16,201.418278 0,197 L0,195 C-5.41083001e-16,190.581722 3.581722,187 8,187 Z M439,187 L700,187 C704.418278,187 708,190.581722 708,195 L708,197 C708,201.418278 704.418278,205 700,205 L439,205 C434.581722,205 431,201.418278 431,197 L431,195 C431,190.581722 434.581722,187 439,187 Z M19.5,0 L163.5,0 C174.269553,-1.97833472e-15 183,8.73044738 183,19.5 C183,30.2695526 174.269553,39 163.5,39 L19.5,39 C8.73044738,39 1.31888981e-15,30.2695526 0,19.5 C-1.31888981e-15,8.73044738 8.73044738,1.97833472e-15 19.5,0 Z M225.5,0 L345.5,0 C356.269553,-1.97833472e-15 365,8.73044738 365,19.5 C365,30.2695526 356.269553,39 345.5,39 L225.5,39 C214.730447,39 206,30.2695526 206,19.5 C206,8.73044738 214.730447,1.97833472e-15 225.5,0 Z"
                fill="#E5EAEC"
              />
            </ContentLoader>
          );
        }}
      </this.Compose>
    );
  }
}

/**
 * PaymentDetailsContainer PropTypes
 * @param id - prop to know payment id
 */
DetailsContainer.propTypes = {
  id: PropTypes.string.isRequired,
};

export default DetailsContainer;
